import React, { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EvidenceModeToggle from "../common/EvidenceModeToggle";
import { siteUrl } from "../../utils/constants";
import {
  ACCENT,
  categoryColor,
  displayTerm,
  GraphData,
  GraphSelection,
  properNounCategories,
} from "./graphShared";
import ExcerptPanel from "./ExcerptPanel";

/** The evidence network view: term co-occurrence from
 *  /api/evidence/graph rendered as an elliptical radial bundle — terms on
 *  a category-grouped rim, relationships as ribbons bundled through the
 *  middle, pinned selections driving the excerpt drawer.
 *
 *  Renders in two homes: the standalone /evidence/graph page (URL-synced
 *  state, mode toggle to /evidence) and embedded inside a case chapter
 *  (embedded prop: self-contained, no URL coupling; the chapter supplies
 *  its own heading and toggle, and onUseSearch flips the chapter's local
 *  mode on small screens). */

const RadialGraph = lazy(() => import("./RadialGraph"));

interface SetInfo {
  name: string;
  passages?: number;
}

interface Option {
  value: string;
  label: string;
}

const styles = `
.evd-lab-controls { display: flex; flex-wrap: wrap; gap: 18px;
  align-items: center; justify-content: center;
  margin: 10px 0 26px; }
.evd-lab-controls .select__container { min-width: 300px; }
.evd-legend { display: flex; flex-wrap: wrap; gap: 8px 10px;
  justify-content: center; margin-bottom: 30px; }
.evd-legend button { display: inline-flex; align-items: center; gap: 7px;
  border: 1px solid #dee2e6; border-radius: 16px; background: #fff;
  padding: 5px 14px; font-size: 13px; color: #212529; cursor: pointer;
  transition: opacity 150ms ease; }
.evd-legend button.off { opacity: 0.35; }
.evd-legend .swatch { width: 11px; height: 11px; border-radius: 50%; }
.evd-stage { position: relative; height: 72vh; min-height: 480px;
  border: 1px solid #dee2e6; border-radius: 4px; background: #fff;
  overflow: hidden; display: flex;
  /* Isolate the SVG stage from the rest of the page so a repaint here
     can never invalidate (or be dropped with) the surrounding layers —
     mitigation for the intermittent white-out on large displays. */
  contain: layout paint; }
.evd-stage-canvas { flex: 1 1 auto; min-width: 0; min-height: 0;
  position: relative; }
/* Small screens: the drawer becomes a bottom sheet under the graph. */
@media (max-width: 768px) {
  .evd-stage { flex-direction: column; height: 80vh; min-height: 420px; }
}
.evd-no-links { position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -50%); max-width: 320px; text-align: center;
  font-size: 14px; color: #757575; background: rgba(255,255,255,0.92);
  border: 1px solid #dee2e6; border-radius: 4px; padding: 14px 18px;
  pointer-events: none; }
.evd-unlinked { font-size: 13px; color: #757575; margin: 10px 2px 0;
  line-height: 1.6; }
.evd-unlinked a { color: inherit; }
.evd-stage-hint { position: absolute; left: 14px; bottom: 10px;
  font-size: 12px; color: #9a9a9a; pointer-events: none; }
.evd-clear-selection { position: absolute; top: 12px; left: 14px;
  z-index: 4; display: inline-flex; align-items: center; gap: 8px;
  border: 1px solid #dee2e6; border-radius: 16px; background: #fff;
  color: #212529; padding: 6px 16px; font-size: 13px; cursor: pointer;
  box-shadow: 0 2px 8px rgba(33,37,41,0.08); }
.evd-clear-selection:hover { background: #f4f2ef; }
/* The pinned term's name carries its category color — the pill's only
   accent, matching the dot it points back to. */
.evd-clear-selection strong { color: var(--evd-sel-color, #212529); }
.evd-lab-loading { position: absolute; inset: 0; display: flex;
  align-items: center; justify-content: center; color: #757575;
  font-size: 15px; }
.evd-mobile-note { text-align: center; padding: 48px 24px; }
.evd-mobile-note p { font-size: 16px; color: #555; margin-bottom: 24px; }
.evd-mobile-note a, .evd-mobile-note button { display: inline-block;
  padding: 12px 28px; border: 0; background: ${ACCENT}; color: #fff;
  border-radius: 4px; text-decoration: none; font-size: 15px;
  cursor: pointer; }
`;

interface EvidenceNetworkProps {
  /** Inside a case chapter: no URL coupling, no page chrome of its own. */
  embedded?: boolean;
  /** Embedded only: flip the surrounding chapter to the search lens. */
  onUseSearch?: () => void;
  /** Default to the smallest collection (fastest first load) instead of
   *  the first listed. */
  preferSmallestSet?: boolean;
}

const EvidenceNetwork = ({
  embedded = false, onUseSearch, preferSmallestSet = false,
}: EvidenceNetworkProps) => {
  const { t } = useTranslation();
  const api = `${siteUrl}/api/evidence`;

  const [sets, setSets] = useState<SetInfo[]>([]);
  const [activeSet, setActiveSet] = useState("");
  const [data, setData] = useState<GraphData | null>(null);
  // Terms with no relationship are not drawn — a dot without links shows
  // nothing a relationship graph is for — but they are listed below it.
  const [unlinked, setUnlinked] = useState<GraphData["nodes"]>([]);
  const [hiddenCategories, setHiddenCategories] = useState<Set<number>>(new Set());
  const [selection, setSelection] = useState<GraphSelection | null>(null);
  const [fullExcerpts, setFullExcerpts] = useState(false);
  const [error, setError] = useState("");
  // The network lens is desktop-only; small screens get pointed at Search.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window.matchMedia === "function"
    && window.matchMedia("(max-width: 768px)").matches);
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  // Context arriving from the search lens (or a shared deep link):
  // consumed once, after the data it refers to loads. Embedded instances
  // are self-contained and never read the page URL.
  const arrivalRef = useRef<{ set: string | null; term: string | null;
    also: string | null; cat: string | null;
    listings: string | null } | null>(
    (() => {
      if (embedded) return null;
      const params = new URLSearchParams(window.location.search);
      return {
        set: params.get("set"),
        term: params.get("term"),
        also: params.get("also"),
        cat: params.get("cat"),
        listings: params.get("listings"),
      };
    })());
  const [listings, setListings] = useState(
    arrivalRef.current?.listings === "1");

  useEffect(() => {
    axios.get(`${api}/sets`)
      .then(({ data: list }: { data: SetInfo[] }) => {
        setSets(list);
        const wanted = arrivalRef.current?.set;
        if (wanted && list.some((s) => s.name === wanted)) {
          setActiveSet(wanted);
        } else if (list.length) {
          setActiveSet(preferSmallestSet
            ? [...list].sort(
              (a, b) => (a.passages ?? 0) - (b.passages ?? 0))[0].name
            : list[0].name);
        }
      })
      .catch(() => setError(
        t("evidence.unavailable", "The evidence service is not available.")));
  }, [api, t]);

  useEffect(() => {
    if (!activeSet) return;
    setData(null);
    setUnlinked([]);
    setSelection(null);
    setHiddenCategories(new Set());
    axios.get(`${api}/graph`,
      { params: { set: activeSet, listings: listings ? 1 : 0 } })
      .then(({ data: full }: { data: GraphData }) => {
        const linked = new Set<string>();
        for (const e of full.edges) { linked.add(e.source); linked.add(e.target); }
        const graph = { ...full,
          nodes: full.nodes.filter((n) => linked.has(n.term)) };
        setUnlinked(full.nodes.filter((n) => !linked.has(n.term)));
        setData(graph);
        const arrival = arrivalRef.current;
        arrivalRef.current = null;
        if (!arrival) return;
        const exists = (term: string | null): term is string =>
          !!term && graph.nodes.some((n) => n.term === term);
        if (exists(arrival.term)) {
          setSelection({
            term: arrival.term,
            alsoTerm: exists(arrival.also) ? arrival.also : undefined,
          });
        } else if (arrival.cat) {
          // A term-less category filter maps to legend focus: dim
          // everything except that category.
          const focus = Number(arrival.cat);
          if (graph.categories.some((c) => c.id === focus)) {
            setHiddenCategories(new Set(graph.categories
              .filter((c) => c.id !== focus).map((c) => c.id)));
          }
        }
      })
      .catch(() => setError(
        t("evidence.unavailable", "The evidence service is not available.")));
  }, [api, activeSet, listings, t]);

  // The current view lives in the URL: a reload restores it, and the mode
  // toggle (or copy-pasting the address) carries it along. Embedded
  // instances leave the case page's URL alone.
  // A pinned term with no co-occurrence edges must say so explicitly:
  // a silently dimmed rim reads as a rendering failure.
  const selectionEdgeless = useMemo(() => {
    if (!data || !selection || selection.alsoTerm) return false;
    return !data.edges.some(
      (e) => e.source === selection.term || e.target === selection.term);
  }, [data, selection]);

  useEffect(() => {
    if (embedded || !activeSet) return;
    const params = new URLSearchParams();
    params.set("set", activeSet);
    if (selection) {
      params.set("term", selection.term);
      if (selection.alsoTerm) params.set("also", selection.alsoTerm);
    }
    if (listings) params.set("listings", "1");
    window.history.replaceState(
      null, "", `${window.location.pathname}?${params.toString()}`);
  }, [embedded, activeSet, selection, listings]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelection(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Full-excerpt mode only exists while something is selected; clearing
  // the selection always lands back on the graph.
  useEffect(() => {
    if (!selection) setFullExcerpts(false);
  }, [selection]);

  const categoryOf = useMemo(() => {
    const map = new Map<string, number>();
    for (const n of data?.nodes || []) map.set(n.term, n.category);
    return map;
  }, [data]);

  // Proper-noun display (Geography terms are stored lowercase).
  const labelOf = useMemo(() => {
    const proper = properNounCategories(data?.categories || []);
    return (term: string) =>
      displayTerm(term, proper.has(categoryOf.get(term) || 0));
  }, [data, categoryOf]);

  const setOptions: Option[] = sets.map(({ name }) => ({
    value: name, label: name,
  }));

  const toggleCategory = (id: number) => {
    setHiddenCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Flip to the search lens keeping everything that has an equivalent
  // there. A pinned pair maps to term + "also mentioning" the second
  // term's CATEGORY (search intersects term × category, not term × term —
  // the closest honest translation).
  const toSearch = (() => {
    const params = new URLSearchParams();
    if (activeSet) params.set("set", activeSet);
    if (selection) {
      params.set("term", selection.term);
      const cat = categoryOf.get(selection.term);
      if (cat) params.set("cat", String(cat));
      if (selection.alsoTerm) {
        const alsoCat = categoryOf.get(selection.alsoTerm);
        if (alsoCat) params.set("also", String(alsoCat));
      }
    }
    if (listings) params.set("listings", "1");
    const query = params.toString();
    return `/evidence${query ? `?${query}` : ""}`;
  })();

  return (
    <>
      <style>{styles}</style>
      {!embedded && (
        <EvidenceModeToggle
          mode="graph"
          toSearch={toSearch}
          toGraph="/evidence/graph"
        />
      )}
      {!embedded && (
        <div className="section-title mb--30 text-center">
          <h3 className="title mt--20">
            {t("evidence.graphSubtitle", "How the evidence connects")}
          </h3>
          <p style={{ fontSize: 15, color: "#757575", marginTop: 8 }}>
            {t("evidence.graphExplainer",
              "Each circle is a term from the coding lexicon, sized by the number "
              + "of archived pages that mention it. Lines join terms that appear "
              + "on the same pages. Hover to see a term's neighborhood; click a "
              + "term — or a line between two terms — to read the excerpts behind it.")}
          </p>
        </div>
      )}

      {error && <p className="evd-count">{error}</p>}

      {!error && isMobile && (
        <div className="evd-mobile-note">
          <p>
            {t("evidence.graphDesktopOnly",
              "The network view is designed for larger screens.")}
          </p>
          {onUseSearch ? (
            <button type="button" onClick={onUseSearch}>
              {t("evidence.useSearch", "Use the search view")}
            </button>
          ) : (
            <Link to={toSearch}>
              {t("evidence.useSearch", "Use the search view")}
            </Link>
          )}
        </div>
      )}

      {!error && !isMobile && (
        <>
          <div className="evd-lab-controls">
            <div className="select__container">
              <Select
                inputId="evidence-lab-set"
                options={setOptions}
                value={setOptions.find((o) => o.value === activeSet) || null}
                onChange={(option) => option && setActiveSet(option.value)}
                className="basic-single"
                classNamePrefix="select"
                aria-label={t("evidence.collection", "Collection")}
              />
            </div>
          </div>

          {data && (
            <div className="evd-legend">
              {data.categories.map(({ id, label }) => (
                <button key={id} type="button"
                  className={hiddenCategories.has(id) ? "off" : ""}
                  aria-pressed={!hiddenCategories.has(id)}
                  onClick={() => toggleCategory(id)}>
                  <span className="swatch"
                    style={{ background: categoryColor(id) }} />
                  {label}
                </button>
              ))}
            </div>
          )}

          <div className="evd-stage">
            <div className="evd-stage-canvas"
              style={fullExcerpts && selection
                ? { display: "none" } : undefined}>
              {data ? (
                <Suspense fallback={(
                  <div className="evd-lab-loading">
                    {t("evidence.loading", "loading…")}
                  </div>
                )}>
                  <RadialGraph
                    data={data}
                    hiddenCategories={hiddenCategories}
                    selection={selection}
                    onSelect={setSelection}
                  />
                </Suspense>
              ) : (
                <div className="evd-lab-loading">
                  {t("evidence.loading", "loading…")}
                </div>
              )}
              {selection && (
                <button type="button" className="evd-clear-selection"
                  style={{
                    ["--evd-sel-color" as string]: categoryColor(
                      categoryOf.get(selection.term) || 0),
                  }}
                  onClick={() => setSelection(null)}>
                  ✕ {t("evidence.clearSelection", "Clear selection")}
                  <strong>
                    {labelOf(selection.term)}
                    {selection.alsoTerm
                      ? ` × ${labelOf(selection.alsoTerm)}` : ""}
                  </strong>
                </button>
              )}
              {selectionEdgeless && (
                <div className="evd-no-links">
                  {t("evidence.noRelationships",
                    "No relationships are recorded for this term in this "
                    + "collection: no other lexicon term appears on the "
                    + "same pages above the display threshold.")}
                </div>
              )}
              <span className="evd-stage-hint">
                {t("evidence.graphHintRadial",
                  "hover a term to isolate its relationships · click "
                  + "terms or ribbons")}
              </span>
            </div>
            <ExcerptPanel
              set={activeSet}
              selection={selection}
              category={selection ? categoryOf.get(selection.term) : undefined}
              onClose={() => setSelection(null)}
              full={fullExcerpts}
              onToggleFull={() => setFullExcerpts((f) => !f)}
              labelOf={labelOf}
              listings={listings}
            />
          </div>
          {unlinked.length > 0 && (
            <p className="evd-unlinked">
              {t("evidence.unlinkedNote", {
                count: unlinked.length,
                defaultValue: "{{count}} terms in this collection appear on "
                  + "too few pages to form a relationship (a link needs at "
                  + "least 3 shared pages) and are not drawn:",
              })}{" "}
              {unlinked.map((n, i) => (
                <React.Fragment key={n.term}>
                  {i > 0 && ", "}
                  <a href={`/evidence?set=${encodeURIComponent(activeSet || "")}`
                    + `&cat=${n.category}&term=${encodeURIComponent(n.term)}`}>
                    {displayTerm(n.term, properNounCategories(
                      data?.categories || []).has(n.category))}</a>
                  {" "}({n.pages})
                </React.Fragment>
              ))}
            </p>
          )}
        </>
      )}
    </>
  );
};

export default EvidenceNetwork;
